// insurance-pdf-generator.service.ts

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Define interface for type safety (matching your component's ProcessedInsurance)
export interface ProcessedInsurance {
  generalInfo: {
    carrier: string;
    employer: string;
    feeSchedule: string;
    groupNumber: string;
    groupName: string;
    subscriberName: string;
    subscriberID: string;
    relationship: string;
    type?: string;
  };
  coveragePercentages: { service: string; percentage: string }[];
  deductibles: { category: string; amount: string; timePeriod: string }[];
  limitations: string[];
}

export class InsurancePdfGenerator {
  static generateInsurancePDF(processedInsuranceInfo: ProcessedInsurance[]): void {
    const pdf = new jsPDF('p', 'mm', 'a4');
    let yPos = 20;
    const pageWidth = pdf.internal.pageSize.getWidth();

    // Set primary color for the header
    pdf.setFontSize(18);
    pdf.setTextColor(66, 0, 238); // Primary color
    pdf.text('Insurance Information', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Process each insurance policy
    processedInsuranceInfo.forEach((insurance, index) => {
      // Check if we have enough space, else start a new page
      if (yPos > 250) {
        pdf.addPage();
        yPos = 20;
      }

      // Section title for this policy
      pdf.setFontSize(14);
      // You can adjust the text color if desired; here we use primary color.
      pdf.setTextColor(66, 0, 238);
      pdf.text(`Policy ${index + 1}: ${insurance.generalInfo.carrier}`, 14, yPos);
      yPos += 8;

      // --- General Information Table ---
      const generalInfoData = [
        ['Insurance Provider', insurance.generalInfo.carrier],
        ['Employer', insurance.generalInfo.employer],
        ['Group Name', insurance.generalInfo.groupName],
        ['Group Number', insurance.generalInfo.groupNumber],
        ['Subscriber', `${insurance.generalInfo.subscriberName} (${insurance.generalInfo.relationship})`],
        ['Subscriber ID', insurance.generalInfo.subscriberID]
      ];

      autoTable(pdf, {
        head: [['Field', 'Value']],
        body: generalInfoData,
        startY: yPos,
        margin: { left: 14, right: 14 },
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [240, 240, 240], textColor: [60, 60, 60] }
      });
      yPos = (pdf as any).lastAutoTable.finalY + 8;

      // --- Coverage Summary Table ---
      if (insurance.coveragePercentages && insurance.coveragePercentages.length > 0) {
        pdf.setFontSize(12);
        // Revert to black for section title if needed
        pdf.setTextColor(0, 0, 0);
        pdf.text('Coverage Summary', 14, yPos);
        yPos += 6;

        const coverageData = insurance.coveragePercentages.map(cov => [
          cov.service,
          `${cov.percentage}%`
        ]);

        autoTable(pdf, {
          head: [['Service', 'Percentage']],
          body: coverageData,
          startY: yPos,
          margin: { left: 14, right: 14 },
          styles: { fontSize: 10, cellPadding: 3 },
          headStyles: { fillColor: [240, 240, 240], textColor: [60, 60, 60] }
        });
        yPos = (pdf as any).lastAutoTable.finalY + 8;
      }

      // --- Deductibles Table ---
      if (insurance.deductibles && insurance.deductibles.length > 0) {
        pdf.setFontSize(12);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Deductibles', 14, yPos);
        yPos += 6;

        const deductiblesData = insurance.deductibles.map(deduct => [
          deduct.category,
          `$${deduct.amount}`,
          deduct.timePeriod
        ]);

        autoTable(pdf, {
          head: [['Category', 'Amount', 'Time Period']],
          body: deductiblesData,
          startY: yPos,
          margin: { left: 14, right: 14 },
          styles: { fontSize: 10, cellPadding: 3 },
          headStyles: { fillColor: [240, 240, 240], textColor: [60, 60, 60] }
        });
        yPos = (pdf as any).lastAutoTable.finalY + 8;
      }

      // --- Limitations List ---
      if (insurance.limitations && insurance.limitations.length > 0) {
        pdf.setFontSize(12);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Limitations', 14, yPos);
        yPos += 6;

        insurance.limitations.forEach(limitation => {
          pdf.setFontSize(10);
          // Use a dark gray text for limitations
          pdf.setTextColor(60, 60, 60);
          pdf.text(`- ${limitation}`, 18, yPos);
          yPos += 5;

          // Check for page break in the limitations list
          if (yPos > 270) {
            pdf.addPage();
            yPos = 20;
          }
        });
        yPos += 8;
      }

      // --- Add a divider between policies ---
      pdf.setDrawColor(200, 200, 200);
      pdf.line(14, yPos, pageWidth - 14, yPos);
      yPos += 8;
    });

    // Save the PDF document
    pdf.save('insurance-info.pdf');
  }
}